import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EyeIcon, MoreHorizontalIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  deleteImportacao,
  fetchImportacaoHistorico,
  fetchImportacaoPublicacaoMapa,
  fetchImportacoes,
  importacaoKeys,
} from '@/services/importacaoService';
import { parseApiError } from '@/utils/apiError';
import { formatDateTime } from '@/utils/date';
import ExcelImportModal from './-ExcelImportModal';
import ImportacaoDadosHistoricoModal from './-ImportacaoDadosHistoricoModal';
import type { IImportacaoItem } from '@/types/importacao';

export const Route = createFileRoute('/_authenticated/importacao/')({
  component: Importacao,
});

const columns: Array<{ key: keyof IImportacaoItem; label: string }> = [
  { key: 'origem', label: 'Origem' },
  { key: 'arquivo', label: 'Arquivo' },
  { key: 'opcao', label: 'Opção' },
  { key: 'colunas', label: 'Colunas' },
  { key: 'unidade_nome', label: 'Unidade' },
  { key: 'status', label: 'Status' },
  { key: 'created_by_nome', label: 'Criado por' },
  { key: 'created_at', label: 'Criado em' },
  { key: 'updated_by_nome', label: 'Editado por' },
  { key: 'updated_at', label: 'Editado em' },
];

function formatValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

/** Exibe até 3 nomes; resto no title (tooltip nativo). */
function formatColunasPreview(colunas: string | null | undefined): {
  short: string;
  full: string | undefined;
} {
  if (colunas === null || colunas === undefined || colunas.trim() === '') {
    return { short: '-', full: undefined };
  }
  const parts = colunas
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return { short: '-', full: undefined };
  const full = parts.join(', ');
  if (parts.length <= 3) return { short: full, full };
  return { short: `${parts.slice(0, 3).join(', ')}...`, full };
}

/** Truncar nome de arquivo em 20 chars; title com o full. */
function formatArquivoPreview(arquivo: string | null | undefined): {
  short: string;
  full: string | undefined;
} {
  if (arquivo === null || arquivo === undefined || arquivo === '') {
    return { short: '-', full: undefined };
  }
  const full = String(arquivo);
  if (full.length <= 20) return { short: full, full };
  return { short: `${full.slice(0, 20)}...`, full };
}

function getHistoricoCustomColumns(items: Array<{ dados: Record<string, unknown> }>): string[] {
  const columnsSet = new Set<string>();
  items.forEach((item) => {
    Object.keys(item?.dados || {}).forEach((key) => columnsSet.add(key));
  });
  return Array.from(columnsSet);
}

function Importacao() {
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [historicoId, setHistoricoId] = useState<string | null>(null);
  const [historicoOrigem, setHistoricoOrigem] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [apagarMapa, setApagarMapa] = useState(false);
  const [motivoExclusao, setMotivoExclusao] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(50);

  const params = { skip, limit };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: importacaoKeys.list(params),
    queryFn: () => fetchImportacoes(params),
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasNextPage = skip + limit < total;

  const historicoQuery = useQuery({
    queryKey: importacaoKeys.historico(historicoId ?? ''),
    queryFn: () => fetchImportacaoHistorico(historicoId!),
    enabled: Boolean(historicoId),
  });
  const historicoItems = historicoQuery.data ?? [];
  const historicoColumns = getHistoricoCustomColumns(historicoItems);

  const publicacaoQuery = useQuery({
    queryKey: importacaoKeys.publicacaoMapa(deleteConfirmId ?? ''),
    queryFn: () => fetchImportacaoPublicacaoMapa(deleteConfirmId!),
    enabled: Boolean(deleteConfirmId),
  });
  const opcaoComercializacao =
    publicacaoQuery.data?.opcao === 'vendas' || publicacaoQuery.data?.opcao === 'recebimento';
  const showApagarMapaCheckbox =
    publicacaoQuery.data?.publicado === true &&
    (publicacaoQuery.data.opcao === 'abate' || publicacaoQuery.data.opcao === 'producao' || opcaoComercializacao);

  const deleteMutation = useMutation({
    mutationFn: ({ id, motivo, apagar_mapa }: { id: string; motivo: string; apagar_mapa: boolean }) =>
      deleteImportacao(id, { motivo, apagar_mapa }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: importacaoKeys.all }),
  });

  const closeDeleteDialog = () => {
    setDeleteConfirmId(null);
    setApagarMapa(false);
    setMotivoExclusao('');
  };

  const onVisualizarHistorico = (item: IImportacaoItem) => {
    setHistoricoId(item.id);
    setHistoricoOrigem(item.origem);
    setShowHistorico(true);
  };

  const onRemover = (item: IImportacaoItem) => {
    setApagarMapa(false);
    setMotivoExclusao('');
    setDeleteConfirmId(item.id);
  };

  const handleModalClose = () => {
    setShowForm(false);
    void queryClient.invalidateQueries({ queryKey: importacaoKeys.all });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId || !motivoExclusao.trim()) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteConfirmId,
        motivo: motivoExclusao.trim(),
        apagar_mapa: showApagarMapaCheckbox && apagarMapa,
      });
      toast.success(
        showApagarMapaCheckbox && apagarMapa
          ? 'Exclusão no MAPA enfileirada. A importação será removida assim que confirmado.'
          : 'Importação excluída com sucesso.',
      );
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao excluir importação'));
    } finally {
      closeDeleteDialog();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Importação</h1>
        <p className="text-on-surface-variant">Gerencie a importação de dados.</p>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex flex-col gap-4 border-b border-outline-variant p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-on-surface">Lista de importações</h2>
          <div className="flex items-center gap-3">
            <Field orientation="horizontal">
              <FieldLabel htmlFor="importacaoLimit">Limite</FieldLabel>
              <Select
                value={String(limit)}
                onValueChange={(value) => {
                  setLimit(Number(value));
                  setSkip(0);
                }}
              >
                <SelectTrigger id="importacaoLimit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Button
              className="bg-success hover:bg-success/90"
              onClick={() => setShowForm(true)}
            >
              Nova importação
            </Button>
          </div>
        </div>

        <div className="p-4">
          {isError && (
            <div
              role="alert"
              className="mb-4 rounded-lg bg-error-container p-4 text-on-error-container"
            >
              {parseApiError(error, 'Erro ao listar importações')}
            </div>
          )}
          {isLoading && (
            <LoadingSpinner
              text="Carregando…"
              size="small"
            />
          )}
          {!isLoading && (
            <>
              <div className="block max-w-full overflow-x-auto overflow-y-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((column) => (
                        <TableHead key={column.key}>{column.label}</TableHead>
                      ))}
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id ?? `${item.arquivo}-${item.created_at}`}>
                        {columns.map((column) => {
                          if (column.key === 'colunas') {
                            const { short, full } = formatColunasPreview(
                              typeof item.colunas === 'string' ? item.colunas : null,
                            );
                            return (
                              <TableCell
                                key={column.key}
                                className="max-w-56"
                              >
                                <span
                                  className="block truncate"
                                  title={full}
                                >
                                  {short}
                                </span>
                              </TableCell>
                            );
                          }
                          if (column.key === 'arquivo') {
                            const { short, full } = formatArquivoPreview(
                              typeof item.arquivo === 'string' ? item.arquivo : null,
                            );
                            return (
                              <TableCell key={column.key}>
                                <span title={full}>{short}</span>
                              </TableCell>
                            );
                          }
                          const raw = item[column.key];
                          const display =
                            column.key === 'created_at' || column.key === 'updated_at'
                              ? formatDateTime(raw)
                              : formatValue(raw);
                          return <TableCell key={column.key}>{display}</TableCell>;
                        })}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Ações"
                              >
                                <MoreHorizontalIcon />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-auto min-w-40"
                            >
                              <DropdownMenuGroup>
                                <DropdownMenuItem
                                  disabled={historicoQuery.isFetching && historicoId === item.id}
                                  onClick={() => onVisualizarHistorico(item)}
                                >
                                  <EyeIcon />
                                  Ver
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  variant="destructive"
                                  onClick={() => onRemover(item)}
                                >
                                  <Trash2Icon />
                                  Remover
                                </DropdownMenuItem>
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + 1}
                          className="p-6 text-center text-on-surface-variant"
                        >
                          Nenhum registro encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between border-t border-outline-variant p-4">
                <Button
                  variant="outline"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-on-surface-variant">
                  Total: {total} | Offset: {skip} | Limite: {limit}
                </span>
                <Button
                  variant="outline"
                  disabled={!hasNextPage}
                  onClick={() => setSkip(skip + limit)}
                >
                  Próximo
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <ExcelImportModal
        isOpen={showForm}
        onClose={handleModalClose}
      />
      <ImportacaoDadosHistoricoModal
        isOpen={showHistorico}
        onClose={() => {
          setShowHistorico(false);
          setHistoricoOrigem(null);
        }}
        items={historicoItems}
        customColumns={historicoColumns}
        origem={historicoOrigem}
      />

      <Dialog
        open={deleteConfirmId != null}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-on-surface-variant">
              Tem certeza que deseja excluir esta importação? Esta ação não pode ser desfeita.
            </p>
            {publicacaoQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-on-surface-variant">
                <Spinner data-icon="inline-start" />
                Verificando publicação no MAPA…
              </div>
            )}
            {publicacaoQuery.isError && (
              <p
                role="alert"
                className="text-sm text-error"
              >
                {parseApiError(publicacaoQuery.error, 'Não foi possível verificar publicação no MAPA.')}
              </p>
            )}
            {showApagarMapaCheckbox && (
              <Field orientation="horizontal">
                <Checkbox
                  id="apagarMapa"
                  checked={apagarMapa}
                  onCheckedChange={(checked) => setApagarMapa(checked === true)}
                />
                <FieldLabel
                  htmlFor="apagarMapa"
                  className="font-normal"
                >
                  Dados já publicados no PGA. Data = {publicacaoQuery.data?.data ?? '-'}. Código do
                  registro MAPA = {publicacaoQuery.data?.cod_mapa ?? '-'}. Deseja excluir o registro
                  do MAPA também?
                  {opcaoComercializacao && (
                    <span className="mt-1 block text-error">
                      Atenção: no MAPA, vendas e compras do período ficam num único registro de
                      comercialização. Excluir aqui apaga LÁ os dois lados (vendas E compras) desse
                      período. Os registros locais do outro tipo continuam ativos no sistema, mas
                      deixam de estar publicados.
                    </span>
                  )}
                </FieldLabel>
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="motivoExclusao">Motivo da exclusão *</FieldLabel>
              <Textarea
                id="motivoExclusao"
                value={motivoExclusao}
                onChange={(e) => setMotivoExclusao(e.target.value)}
                placeholder="Descreva o motivo da exclusão desta importação."
                rows={3}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDelete()}
              disabled={deleteMutation.isPending || publicacaoQuery.isLoading || !motivoExclusao.trim()}
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner data-icon="inline-start" />
                  Excluindo…
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
